import { useState, type MouseEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bike, Check, CircleCheck } from "lucide-react";
import { LoadingSkeleton } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import type { DispatchBoardResponse } from "@/lib/api/dispatchTypes";
import { useMoney } from "@/shared/ui";
import { dispatchChip, type BillDispatch } from "@/modules/pos/lib/dispatchBoard";
import { CommandIds } from "@/modules/pos/lib/assignDrivers";
import { paymentLine, waitingTotal, type PickupRow, type WaitingRow } from "@/modules/pos/lib/deliveryWaiting";
import { dispatchQueryKeys } from "@/modules/pos/lib/useDispatchBoard";
import { DispatchStatusChip } from "../dispatch/DispatchStatusChip";
import { AssignDriversPanel } from "./AssignDriversPanel";
import { DeliveryAddressLine } from "./DeliveryAddressLine";
import { ItemsToggle, LaneAge, OrderNumber, toggleIn } from "./DeliveryLane";
import { WaitingOrderLines } from "./WaitingOrderLines";

/**
 * «بانتظار مندوب» — the Delivery tab's first lane (B6 · P24, plan D-36 / D-37 /
 * D-38; drawn as a lane of cards in the 2026-10-26 redesign): one card per own
 * bag nobody holds yet — the number (opens the bill), who and where, how long
 * since it opened, an offer's countdown, the total and how it is paid, and
 * Assign (or Change while an offer is out).
 *
 * - Tapping a card toggles its checkbox; the number, the name and Assign never
 *   do. "Assign N" in the lane head opens the panel on the selection.
 * - Bags not sent to the kitchen yet (`unsent`) close the lane, muted, with
 *   Assign held until the food is sent.
 * - Read-only for a driver-cash confirmer without `DISPATCH_MANAGE`: the cards,
 *   no checkbox and no assign (plan §7).
 *
 * The rows come sorted (`waitingRows`: kitchen READY first, then oldest).
 */
export function WaitingForDriverCard({
  rows,
  loading = false,
  error = null,
  canManage,
  lookup,
  board,
  now,
  skewMs,
  canOpen = false,
  unsent = [],
}: {
  rows: readonly WaitingRow[];
  /** Own bags with nothing sent to the kitchen yet: shown muted, not assignable. */
  unsent?: readonly PickupRow[];
  /** The dispatch board has not answered yet. */
  loading?: boolean;
  /**
   * G24-M3: the board could not be read and nothing is cached — the card says
   * so with a retry instead of guessing which bag has a driver.
   */
  error?: unknown;
  canManage: boolean;
  lookup: (invoiceId: string) => BillDispatch | undefined;
  board: DispatchBoardResponse | undefined;
  /** The board's clock (seconds while an offer counts down). */
  now: number;
  skewMs: number;
  /**
   * 2026-10-20 ("I can't open those invoices"): the order number and the
   * Deliver-to cell open the bill — the route «Open bill» takes elsewhere.
   * The checkbox, the Items toggle and Assign never navigate.
   */
  canOpen?: boolean;
}) {
  const t = useTranslate();
  const money = useMoney();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { locale = "ar" } = useParams();
  const openBill = (event: MouseEvent, invoiceId: string) => {
    event.stopPropagation();
    // The address line's map link opens the map, not the bill.
    if ((event.target as HTMLElement).closest("a")) return;
    navigate(`/${locale}/pos/invoices/${invoiceId}`);
  };
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [panelFor, setPanelFor] = useState<string[] | null>(null);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const itemsFor = (bill: WaitingRow["bill"], prefix: string) => {
    const id = bill.invoiceId;
    const seq = bill.dailySequence;
    return {
      toggle: (
        <ItemsToggle
          seq={seq}
          count={bill.plates?.chargeable ?? 0}
          open={expanded.has(id)}
          onToggle={() => setExpanded((current) => toggleIn(current, id))}
          testId={`${prefix}-items-${seq}`}
        />
      ),
      lines: expanded.has(id) ? (
        <div data-ui="dl-lines" onClick={(event) => event.stopPropagation()}>
          <WaitingOrderLines invoiceId={id} testId={`${prefix}-lines-${seq}`} />
        </div>
      ) : null,
    };
  };
  /* One id per retry key, kept across panel openings so a lost answer is
     retried with the same id (D-10). */
  const [ids] = useState(() => new CommandIds());

  // Only rows still waiting (and not offered) can stay picked.
  const pickable = rows.filter((row) => !row.offered);
  const picked = pickable.filter((row) => selected.has(row.bill.invoiceId));
  const np = picked.length;

  const toggle = (invoiceId: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  const openPanel = (event: MouseEvent | null, invoiceIds: string[]) => {
    event?.stopPropagation();
    setPanelFor(invoiceIds);
  };

  const panelBills = panelFor
    ? panelFor
        .map((id) => rows.find((row) => row.bill.invoiceId === id)?.bill)
        .filter((bill): bill is WaitingRow["bill"] => bill !== undefined)
    : [];

  return (
    <section data-ui="dl-lane" data-lane="waiting" data-testid="waiting-card" aria-label={t((m) => m.dispatch.waitingTitle)} className="flex min-w-0 flex-col">
      <header data-ui="dl-lane-head" className="flex items-center">
        <span aria-hidden="true" data-ui="dl-dot" data-tone="red" />
        <h2 data-ui="dl-lane-title">{t((m) => m.dispatch.waitingTitle)}</h2>
        <span data-ui="dl-count" data-testid="waiting-count" className="tabular-nums">
          {loading || error ? "" : rows.length + unsent.length}
        </span>
        <span className="flex-1" />
        {canManage && np > 0 && !error ? (
          <button
            type="button"
            data-ui="dl-btn"
            data-tone="primary"
            data-testid="waiting-assign-selected"
            onClick={() => openPanel(null, picked.map((row) => row.bill.invoiceId))}
            className="flex items-center"
          >
            <Bike aria-hidden="true" size={14} strokeWidth={2} />
            <span>
              {t(
                countedKey(np, {
                  one: (m) => m.dispatch.waitingAssignSelectedOne,
                  two: (m) => m.dispatch.waitingAssignSelectedTwo,
                  few: (m) => m.dispatch.waitingAssignSelectedFew,
                  many: (m) => m.dispatch.waitingAssignSelected,
                }),
                { count: np },
              )}
            </span>
          </button>
        ) : null}
      </header>
      {canManage && rows.length > 1 && !error ? (
        <p data-ui="dl-lane-hint">{t((m) => m.dispatch.laneWaitingHint)}</p>
      ) : null}

      {error ? (
        <p data-ui="waiting-error" role="alert" data-testid="waiting-error" className="flex flex-wrap items-center">
          <span>{t((m) => m.dispatch.sheetLoadFailed)}</span>
          <button
            type="button"
            data-ui="waiting-retry"
            data-testid="waiting-retry"
            onClick={() => void queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.board })}
          >
            {t((m) => m.common.retry)}
          </button>
        </p>
      ) : loading ? (
        <div data-ui="waiting-loading" aria-busy="true" aria-label={t((m) => m.common.loading)}>
          <LoadingSkeleton rows={3} />
        </div>
      ) : rows.length === 0 && unsent.length === 0 ? (
        <p data-ui="dl-empty" data-testid="waiting-empty" className="flex items-center justify-center">
          <CircleCheck aria-hidden="true" size={16} strokeWidth={2} />
          <span>{t((m) => m.dispatch.waitingEmpty)}</span>
        </p>
      ) : (
        <div data-ui="dl-cards" role="list" aria-label={t((m) => m.dispatch.waitingTitle)} className="flex flex-col">
          {rows.map((row) => {
            const { bill } = row;
            const id = bill.invoiceId;
            const seq = bill.dailySequence;
            const selectable = canManage && !row.offered;
            const isPicked = selectable && selected.has(id);
            const chip = dispatchChip(row.entry, now, skewMs);
            const pay = paymentLine(bill);
            const items = itemsFor(bill, "waiting");
            return (
              <article
                key={id}
                role="listitem"
                data-ui="dl-card"
                data-picked={isPicked ? "" : undefined}
                data-selectable={selectable ? "" : undefined}
                data-testid={`waiting-row-${seq}`}
                onClick={selectable ? () => toggle(id) : undefined}
                className="flex flex-col"
              >
                <div data-ui="dl-row" className="flex items-center">
                  {selectable ? (
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isPicked}
                      aria-label={t((m) => m.dispatch.waitingSelect, { sequence: seq })}
                      data-ui="waiting-check"
                      data-checked={isPicked ? "" : undefined}
                      data-testid={`waiting-check-${seq}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggle(id);
                      }}
                    >
                      {isPicked ? <Check aria-hidden="true" size={13} strokeWidth={3} /> : null}
                    </button>
                  ) : null}
                  <OrderNumber seq={seq} canOpen={canOpen} testId={`waiting-open-${seq}`} onOpen={(event) => openBill(event, id)} />
                  <span
                    data-ui="dl-name"
                    data-openable={canOpen ? "" : undefined}
                    data-testid={`waiting-to-${seq}`}
                    onClick={canOpen ? (event) => openBill(event, id) : undefined}
                    className="min-w-0 truncate"
                  >
                    <bdi>{bill.customerName?.trim() || t((m) => m.dispatch.waitingNoName)}</bdi>
                  </span>
                  <span className="flex-1" />
                  <LaneAge since={bill.openedAt} now={now} />
                </div>
                <DeliveryAddressLine bill={bill} testId={`waiting-address-${seq}`} />
                {chip ? (
                  <span className="flex">
                    <DispatchStatusChip chip={chip} testId={`waiting-chip-${seq}`} />
                  </span>
                ) : null}
                <div data-ui="dl-row" className="flex items-center">
                  <span data-ui="dl-money" className="tabular-nums" data-testid={`waiting-total-${seq}`}>
                    {money(waitingTotal(bill))}
                  </span>
                  {pay ? (
                    <span data-ui="dl-pay" data-testid={`waiting-pay-${seq}`}>
                      {pay === "cod" ? t((m) => m.dispatch.payCashOnDelivery) : t((m) => m.dispatch.payPaid)}
                    </span>
                  ) : null}
                  <span className="flex-1" />
                  {items.toggle}
                </div>
                {items.lines}
                {canManage ? (
                  <button
                    type="button"
                    data-ui="dl-btn"
                    data-wide=""
                    data-tone={row.offered ? "quiet" : "primary"}
                    data-kind={row.offered ? "change" : "assign"}
                    data-testid={`waiting-assign-${seq}`}
                    onClick={(event) => openPanel(event, [id])}
                    className="flex items-center justify-center"
                  >
                    <Bike aria-hidden="true" size={14} strokeWidth={2} />
                    <span>{row.offered ? t((m) => m.dispatch.actionChange) : t((m) => m.dispatch.actionAssign)}</span>
                  </button>
                ) : null}
              </article>
            );
          })}
          {/* Not sent to the kitchen yet (G24b M3): the lane shows them, muted,
              with Assign held until the food is sent. */}
          {unsent.map(({ bill }) => {
            const id = bill.invoiceId;
            const seq = bill.dailySequence;
            const pay = paymentLine(bill);
            const items = itemsFor(bill, "unsent");
            return (
              <article
                key={id}
                role="listitem"
                data-ui="dl-card"
                data-muted=""
                data-testid={`unsent-row-${seq}`}
                className="flex flex-col"
              >
                <div data-ui="dl-row" className="flex items-center">
                  <OrderNumber seq={seq} canOpen={canOpen} testId={`unsent-open-${seq}`} onOpen={(event) => openBill(event, id)} />
                  <span data-ui="dl-name" className="min-w-0 truncate">
                    <bdi>{bill.customerName?.trim() || t((m) => m.dispatch.waitingNoName)}</bdi>
                  </span>
                  <span className="flex-1" />
                  <LaneAge since={bill.openedAt} now={now} />
                </div>
                <DeliveryAddressLine bill={bill} testId={`unsent-address-${seq}`} />
                <span data-ui="dl-pill" data-tone="grey" className="self-start">
                  {t((m) => m.dispatch.laneUnsentChip)}
                </span>
                <div data-ui="dl-row" className="flex items-center">
                  <span data-ui="dl-money" className="tabular-nums">{money(waitingTotal(bill))}</span>
                  {pay ? (
                    <span data-ui="dl-pay">
                      {pay === "cod" ? t((m) => m.dispatch.payCashOnDelivery) : t((m) => m.dispatch.payPaid)}
                    </span>
                  ) : null}
                  <span className="flex-1" />
                  {items.toggle}
                </div>
                {items.lines}
                {canManage ? (
                  <button
                    type="button"
                    data-ui="dl-btn"
                    data-wide=""
                    data-tone="primary"
                    disabled
                    title={t((m) => m.dispatch.laneUnsentTip)}
                    data-testid={`unsent-assign-${seq}`}
                    className="flex items-center justify-center"
                  >
                    <Bike aria-hidden="true" size={14} strokeWidth={2} />
                    <span>{t((m) => m.dispatch.actionAssign)}</span>
                  </button>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {panelFor && panelBills.length > 0 ? (
        <AssignDriversPanel
          key={panelFor.join(",")}
          bills={panelBills}
          lookup={lookup}
          board={board}
          ids={ids}
          onAssigned={(invoiceIds) =>
            setSelected((current) => {
              const next = new Set(current);
              for (const invoiceId of invoiceIds) next.delete(invoiceId);
              return next;
            })
          }
          onClose={() => setPanelFor(null)}
        />
      ) : null}
    </section>
  );
}
